import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Crown, Edit, Power } from "lucide-react";
import { ConnectedUserFormModal } from "./ConnectedUserFormModal";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";

interface ConnectedUser {
  id: string;
  name: string;
  cpf: string;
  work_area: string;
  phone: string | null;
  email: string;
  is_active: boolean;
  permissions: string[];
}

interface LimitInfo {
  can_add: boolean;
  current_count: number;
  max_allowed: number;
  plan_code: string;
}

const PERMISSION_LABELS: Record<string, string> = {
  view: "Ver",
  create: "Criar",
  update: "Editar",
  delete: "Excluir",
};

const PERMISSION_COLORS: Record<string, string> = {
  view: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  create: "bg-green-500/10 text-green-700 dark:text-green-400",
  update: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
  delete: "bg-red-500/10 text-red-700 dark:text-red-400",
};

export function ConnectedUsersManager() {
  const { toast } = useToast();
  const [users, setUsers] = useState<ConnectedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [limitInfo, setLimitInfo] = useState<LimitInfo | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ConnectedUser | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    await Promise.all([checkLimit(), loadUsers()]);
  }

  async function checkLimit() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc("check_connected_users_limit", {
        _owner_id: user.id,
      });

      if (error) throw error;
      if (data && data.length > 0) {
        setLimitInfo(data[0]);
      }
    } catch (error: any) {
      console.error("Error checking limit:", error);
    }
  }

  async function loadUsers() {
    try {
      setLoading(true);
      const { data: usersData, error: usersError } = await supabase
        .from("connected_users")
        .select("*")
        .order("created_at", { ascending: false });

      if (usersError) throw usersError;

      const usersWithPermissions = await Promise.all(
        (usersData || []).map(async (user) => {
          const { data: perms } = await supabase
            .from("user_permissions")
            .select("permission")
            .eq("connected_user_id", user.id);

          return {
            ...user,
            permissions: perms?.map((p) => p.permission) || [],
          };
        })
      );

      setUsers(usersWithPermissions);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar usuários",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleAddUser() {
    if (limitInfo && !limitInfo.can_add) {
      setShowUpgradeModal(true);
    } else {
      setEditingUser(null);
      setShowAddModal(true);
    }
  }

  function handleEditUser(user: ConnectedUser) {
    setEditingUser(user);
    setShowAddModal(true);
  }

  async function handleToggleActive(user: ConnectedUser) {
    try {
      const { error } = await supabase
        .from("connected_users")
        .update({ is_active: !user.is_active })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: user.is_active ? "Usuário desativado" : "Usuário ativado",
        description: `${user.name} foi ${user.is_active ? "desativado" : "ativado"}.`,
      });

      loadUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  function getNextPlan() {
    if (!limitInfo) return "plus";
    const planOrder = ["free", "starter", "plus", "professional", "doctor"];
    const currentIndex = planOrder.indexOf(limitInfo.plan_code);
    return planOrder[Math.min(currentIndex + 1, planOrder.length - 1)];
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const progressValue = limitInfo
    ? (limitInfo.current_count / limitInfo.max_allowed) * 100
    : 0;

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Usuários Conectados</CardTitle>
              <CardDescription>
                Plano {limitInfo?.plan_code}: {limitInfo?.current_count} de{" "}
                {limitInfo?.max_allowed} usuários
              </CardDescription>
            </div>
            {limitInfo?.can_add ? (
              <Button onClick={handleAddUser}>
                <UserPlus className="h-4 w-4 mr-2" />
                Adicionar Usuário
              </Button>
            ) : (
              <Button onClick={() => setShowUpgradeModal(true)}>
                <Crown className="h-4 w-4 mr-2" />
                Fazer Upgrade
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Progress value={progressValue} className="h-2" />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                Nenhum usuário conectado cadastrado
              </p>
              <Button onClick={handleAddUser}>
                <UserPlus className="h-4 w-4 mr-2" />
                Adicionar Primeiro Usuário
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Área de Atuação</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {user.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .substring(0, 2)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">
                            CPF: {user.cpf}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.work_area}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.permissions.map((perm) => (
                          <Badge
                            key={perm}
                            variant="outline"
                            className={PERMISSION_COLORS[perm]}
                          >
                            {PERMISSION_LABELS[perm]}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? "default" : "secondary"}>
                        {user.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                        >
                          <Power
                            className={`h-4 w-4 ${user.is_active ? "text-green-600" : "text-gray-400"
                              }`}
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConnectedUserFormModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        editingUser={editingUser}
        onSuccess={() => {
          setShowAddModal(false);
          loadData();
        }}
      />

      {showUpgradeModal && (
        <SubscriptionModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          planCode={getNextPlan()}
          planName=""
          planPrice={0}
          onSuccess={() => {
            setShowUpgradeModal(false);
            loadData();
          }}
        />
      )}
    </>
  );
}
